import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { NotifyForm } from "@/components/shop/NotifyForm";
import { Stars, formatRating } from "@/components/reviews/Stars";
import { formatEUR } from "@/lib/format";
import { EASE } from "@/lib/motion";

const trackSpot = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
};

export const ProductCard = ({ product, index = 0 }) => {
  const { add } = useCart();
  const soldOut = product.stock <= 0;
  const href = `/prodotto/${product.slug}`;

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, delay: (index % 5) * 0.07, ease: EASE }}
      className="group"
      data-testid={`product-card-${product.slug}`}
    >
      <div onMouseMove={trackSpot} className="spotlight relative aspect-[4/5] overflow-hidden rounded-[1.75rem] bg-parchment ring-1 ring-copper/20 transition-shadow duration-500 group-hover:shadow-[0_30px_60px_-30px_rgba(15,30,51,0.45)]">
        <Link to={href} data-testid={`product-card-link-${product.slug}`} aria-label={`Scopri ${product.name}`}>
          <img src={product.image} alt={`Perfero ${product.name}`} loading="lazy" className={`h-full w-full object-cover transition-[transform,opacity] duration-[1400ms] ease-out group-hover:scale-[1.04] ${soldOut ? "opacity-60 grayscale-[35%]" : ""}`} />
        </Link>
      </div>
      <div className="mt-5 flex items-start justify-between gap-4">
        <Link to={href} className="min-w-0">
          <h3 className="font-display text-2xl leading-tight text-navy transition-colors group-hover:text-copper-deep">{product.name}</h3>
          <p className="mt-1 text-[15px] text-slate2">{product.category} · {product.size}</p>
          {product.rating_count > 0 && (
            <p className="mt-2 flex items-center gap-2 text-[14px] text-slate2" data-testid={`product-rating-${product.slug}`}>
              <Stars value={product.rating_average} /> {formatRating(product.rating_average)} ({product.rating_count})
            </p>
          )}
          <p className="mt-2 font-display text-2xl text-navy" data-testid={`product-price-${product.slug}`}>
            {formatEUR(product.price)}
          </p>
        </Link>
        <button
          type="button"
          disabled={soldOut}
          onClick={() => add(product)}
          aria-label={`Aggiungi ${product.name} al carrello`}
          data-testid={`quick-add-${product.slug}`}
          className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-navy text-ivory shadow-lg transition-[transform,background-color] duration-300 hover:scale-110 hover:bg-copper-deep disabled:opacity-40"
        >
          <Plus className="h-5 w-5" />
        </button>
      </div>
      {soldOut ? (
        <>
          <p className="mt-2 text-[15px] font-medium uppercase tracking-[0.16em] text-copper-deep" data-testid={`product-unavailable-${product.slug}`}>Non disponibile</p>
          <NotifyForm product={product} compact />
        </>
      ) : (
        <p className="mt-2 text-[15px] italic text-slate2">{product.tagline}</p>
      )}
    </motion.article>
  );
};
